import { RESPONSIVE_IMAGE_DEFAULTS } from '@/app/components/media/app-image/responsive-image/constants';
import { type ResponsiveImageSource } from '@/app/components/media/app-image/responsive-image/types';
import { isSvgSrc } from '@/app/components/media/app-image/shared/image-source';
import { toStableImageKitParamsKey } from '@/app/components/media/app-image/shared/imagekit-transform';
import { type ImageKitUrlOptions } from '@/app/components/media/app-image/shared/imagekit-types';
import { buildProjectImageUrl } from '@/app/components/media/app-image/shared/project-adapter';

const MOBILE_MAX_WIDTH = 1023;

export const isMobileMedia = (media?: string): boolean => {
  if (!media) return false;
  const trimmedMedia = media.trim();
  if (trimmedMedia === RESPONSIVE_IMAGE_DEFAULTS.media.mobile) return true;

  const maxWidthMatch = /max-width\s*:\s*(\d+)px/i.exec(trimmedMedia);
  return !!(maxWidthMatch && Number(maxWidthMatch[1]) <= MOBILE_MAX_WIDTH);
};

const parseMinWidth = (media?: string): number | null =>
  media
    ? Number(/min-width\s*:\s*(\d+)px/.exec(media)?.[1] ?? NaN) || null
    : null;

export const getDefaultWidthsForMedia = (media?: string): readonly number[] =>
  isMobileMedia(media)
    ? RESPONSIVE_IMAGE_DEFAULTS.widths.mobile
    : RESPONSIVE_IMAGE_DEFAULTS.widths.desktop;

const applyResponsiveSourceDefaults = (
  source: ResponsiveImageSource,
  index: number,
  total: number
): ResponsiveImageSource => {
  const media =
    source.media ??
    (index === 0 && total > 1
      ? RESPONSIVE_IMAGE_DEFAULTS.media.mobile
      : RESPONSIVE_IMAGE_DEFAULTS.media.desktop);
  const mobile = isMobileMedia(media);
  return {
    ...source,
    media,
    sizes:
      source.sizes ??
      (mobile
        ? RESPONSIVE_IMAGE_DEFAULTS.sizes.mobile
        : RESPONSIVE_IMAGE_DEFAULTS.sizes.desktop),
    widths:
      source.widths ??
      (mobile
        ? RESPONSIVE_IMAGE_DEFAULTS.widths.mobile
        : RESPONSIVE_IMAGE_DEFAULTS.widths.desktop),
  };
};

export const buildResponsiveImageUrl = (
  source: Pick<ResponsiveImageSource, 'src' | 'params'>,
  options: Pick<ImageKitUrlOptions, 'defaultTransforms'> = {}
): string => {
  if (isSvgSrc(source.src)) return source.src;

  return buildProjectImageUrl(source.src, {
    defaultTransforms: options.defaultTransforms,
    params: source.params,
  });
};

export const getResponsiveImageFallbackUrl = (
  source: ResponsiveImageSource | null,
  options: Pick<ImageKitUrlOptions, 'defaultTransforms'> = {}
): string => {
  if (!source) return '';

  return buildResponsiveImageUrl(source, options);
};

export const buildResponsiveImageSrcSet = (
  baseSrc: string,
  widths?: readonly number[],
  params?: ResponsiveImageSource['params'],
  options: Pick<ImageKitUrlOptions, 'defaultTransforms'> = {}
): string => {
  if (isSvgSrc(baseSrc)) return baseSrc;

  const widthCandidates = (
    widths?.length ? widths : RESPONSIVE_IMAGE_DEFAULTS.widths.desktop
  ) as number[];
  return widthCandidates
    .map(
      (width) =>
        `${buildProjectImageUrl(baseSrc, {
          defaultTransforms: options.defaultTransforms,
          params: { ...(params ?? {}), w: width },
        })} ${width}w`
    )
    .join(', ');
};

export const getResponsiveImageSourceKey = (
  source: ResponsiveImageSource
): string =>
  [
    source.media ?? '',
    source.src,
    source.sizes ?? '',
    source.widths?.join(',') ?? '',
    toStableImageKitParamsKey(source.params),
  ].join('|');

export const getResponsiveImageKey = (
  sources: ResponsiveImageSource[]
): string => sources.map(getResponsiveImageSourceKey).join('||');

export const pickFallback = (
  sources: ResponsiveImageSource[]
): ResponsiveImageSource | null => {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  return (
    sources.find((s) => !s.media) ??
    sources.find(
      (s) => s.media?.trim() === RESPONSIVE_IMAGE_DEFAULTS.media.desktop
    ) ??
    sources
      .map((s) => ({ s, mw: parseMinWidth(s.media) }))
      .filter(
        (x): x is { s: ResponsiveImageSource; mw: number } => x.mw !== null
      )
      .sort((a, b) => b.mw - a.mw)[0]?.s ??
    sources[sources.length - 1]!
  );
};

const normalizeSources = (
  arr: ResponsiveImageSource[]
): ResponsiveImageSource[] => {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.map((source, index) =>
    applyResponsiveSourceDefaults(source, index, arr.length)
  );
};

export const toEffectiveSources = (args: {
  mobileSrc?: ResponsiveImageSource;
  desktopSrc?: ResponsiveImageSource;
  sources?: ResponsiveImageSource[];
}): ResponsiveImageSource[] => {
  if (args.sources?.length) return normalizeSources([...args.sources]);
  const list: ResponsiveImageSource[] = [];
  if (args.mobileSrc)
    list.push(applyResponsiveSourceDefaults(args.mobileSrc, 0, 2));
  if (args.desktopSrc)
    list.push(
      applyResponsiveSourceDefaults(
        args.desktopSrc,
        list.length ? 1 : 0,
        list.length ? 2 : 1
      )
    );
  return list;
};
